import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Home, Building2, Check, ChevronRight, Clock, CheckCircle2 } from "lucide-react";

const STEPS = ["step_branch", "step_service", "step_time", "step_info", "step_review"];

export default function Booking() {
  const { t, localize, fmtSAR, fmtDate, isAr } = useI18n();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);

  const [locationType, setLocationType] = useState("branch");
  const [branchId, setBranchId] = useState("");
  const [serviceId, setServiceId] = useState(sp.get("service") || "");
  const [service, setService] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [addonIds, setAddonIds] = useState([]);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [start, setStart] = useState("");
  const [info, setInfo] = useState({ customer_name: "", customer_phone: "", address: "", notes: "" });
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [payChoice, setPayChoice] = useState("deposit");
  const [payMethod, setPayMethod] = useState("pay_on_arrival");
  const [coupon, setCoupon] = useState("");
  const [quote, setQuote] = useState(null);
  const [hold, setHold] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); if (!branchId && r.data[0]) setBranchId(r.data[0].id); }).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (serviceId) api.get(`/services/${serviceId}`).then((r) => setService(r.data)).catch(() => {});
  }, [serviceId]);

  // countdown for hold
  useEffect(() => {
    if (!hold) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(hold.expires_at) - new Date()) / 1000));
      setCountdown(left);
      if (left === 0) { toast.error(isAr ? "انتهت مهلة الحجز المؤقت" : "Hold expired"); setHold(null); setStep(2); }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [hold, isAr]);

  const loadSlots = useCallback((d) => {
    setDate(d); setStart("");
    if (branchId && serviceId && d)
      api.get(`/availability?branch_id=${branchId}&service_id=${serviceId}&date=${d}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [branchId, serviceId]);

  const toggleAddon = (id) => setAddonIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const refreshQuote = useCallback(() => {
    if (!serviceId || !start) return;
    api.post("/quote", { service_id: serviceId, package_id: packageId, addon_ids: addonIds, location_type: locationType, start, coupon_code: coupon || null })
      .then((r) => setQuote(r.data)).catch(() => {});
  }, [serviceId, start, packageId, addonIds, locationType, coupon]);

  useEffect(() => { if (step === 4) refreshQuote(); }, [step, refreshQuote]);

  const goReview = async () => {
    if (!info.customer_name || !info.customer_phone) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/holds", { branch_id: branchId, service_id: serviceId, package_id: packageId, addon_ids: addonIds, location_type: locationType, start, coupon_code: coupon || null });
      setHold(data); setQuote(data.quote); setStep(4);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!policyAccepted) { toast.error(isAr ? "يجب قبول السياسة" : "Accept the policy"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/confirm", { hold_id: hold.id, ...info, policy_accepted: true, payment_method: payMethod, payment_choice: payChoice });
      nav(`/booking/confirmed/${data.id}`);
    } catch (e) { toast.error(apiError(e)); if (String(e?.response?.status) === "410") setStep(2); }
    finally { setBusy(false); }
  };

  const canNext = [() => branchId || locationType === "home", () => serviceId, () => start, () => info.customer_name && info.customer_phone, () => policyAccepted][step];

  const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("booking.title")}</h1>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-1 overflow-x-auto no-scrollbar pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${i === step ? "bg-primary text-white" : i < step ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400"}`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>
              {t(`booking.${s}`)}
            </div>
            {i < STEPS.length - 1 && <ChevronRight className={`h-4 w-4 shrink-0 text-slate-300 ${isAr ? "rotate-180" : ""}`} />}
          </React.Fragment>
        ))}
      </div>

      {hold && step === 4 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-[hsl(var(--brand-orange))]/10 px-4 py-2.5 text-sm font-medium text-[hsl(var(--brand-orange))]" data-testid="hold-timer">
          <Clock className="h-4 w-4" /> {t("booking.hold_note")} {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-border bg-white p-5 sm:p-7">
        {step === 0 && (
          <div>
            <div className="grid grid-cols-2 gap-3">
              {[["branch", Building2, t("booking.at_branch")], ["home", Home, t("booking.at_home")]].map(([v, Icon, label]) => (
                <button key={v} onClick={() => setLocationType(v)} data-testid={`location-${v}`} className={`flex flex-col items-center gap-2 rounded-xl border-2 p-6 transition-all ${locationType === v ? "border-primary bg-primary/5" : "border-border hover:border-slate-300"}`}>
                  <Icon className={`h-7 w-7 ${locationType === v ? "text-primary" : "text-slate-400"}`} /><span className="font-semibold">{label}</span>
                </button>
              ))}
            </div>
            {locationType === "branch" && (
              <div className="mt-6 space-y-3">
                <label className="text-sm font-medium text-slate-700">{t("booking.choose_branch")}</label>
                {branches.map((b) => (
                  <button key={b.id} onClick={() => setBranchId(b.id)} data-testid={`branch-opt-${b.id}`} className={`flex w-full items-center justify-between rounded-xl border-2 p-4 text-start transition-all ${branchId === b.id ? "border-primary bg-primary/5" : "border-border hover:border-slate-300"}`}>
                    <span><span className="font-semibold text-slate-900">{localize(b, "name")}</span><span className="block text-sm text-slate-500">{localize(b, "city")}</span></span>
                    {branchId === b.id && <Check className="h-5 w-5 text-primary" />}
                  </button>
                ))}
              </div>
            )}
            {locationType === "home" && <p className="mt-5 rounded-xl bg-amber-50 p-4 text-sm text-amber-800">{isAr ? "تُضاف رسوم التصوير المنزلي (300 ر.س) إلى الفاتورة." : "A home photography fee (SAR 300) will be added."}</p>}
          </div>
        )}

        {step === 1 && (
          <div>
            <label className="text-sm font-medium text-slate-700">{t("booking.choose_service")}</label>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {services.map((s) => (
                <button key={s.id} onClick={() => { setServiceId(s.id); setPackageId(null); }} data-testid={`service-opt-${s.id}`} className={`flex items-center gap-3 rounded-xl border-2 p-3 text-start transition-all ${serviceId === s.id ? "border-primary bg-primary/5" : "border-border hover:border-slate-300"}`}>
                  <img src={s.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
                  <span className="flex-1"><span className="block font-semibold text-slate-900">{localize(s, "name")}</span><span className="text-sm text-primary">{fmtSAR(s.price)}</span></span>
                </button>
              ))}
            </div>
            {service?.packages?.length > 0 && (
              <div className="mt-6">
                <label className="text-sm font-medium text-slate-700">{t("booking.choose_package")}</label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {service.packages.map((p) => (
                    <button key={p.id} onClick={() => setPackageId(packageId === p.id ? null : p.id)} data-testid={`package-opt-${p.id}`} className={`rounded-xl border-2 p-4 text-start transition-all ${packageId === p.id ? "border-primary bg-primary/5" : "border-border hover:border-slate-300"}`}>
                      <span className="block font-semibold text-slate-900">{localize(p, "name")}</span><span className="text-sm text-slate-500">{localize(p, "desc")}</span><span className="mt-1 block font-bold text-primary">{fmtSAR(p.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {service?.addons?.length > 0 && (
              <div className="mt-6">
                <label className="text-sm font-medium text-slate-700">{t("booking.addons")}</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {service.addons.map((a) => (
                    <button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`addon-opt-${a.id}`} className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-all ${addonIds.includes(a.id) ? "border-[hsl(var(--brand-orange))] bg-[hsl(var(--brand-orange))]/10 text-[hsl(var(--brand-orange))]" : "border-border text-slate-600"}`}>
                      {localize(a, "name")} · {fmtSAR(a.price)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <label className="text-sm font-medium text-slate-700">{t("booking.choose_date")}</label>
            <input type="date" min={minDate} value={date} onChange={(e) => loadSlots(e.target.value)} data-testid="booking-date-input" className="mt-2 block w-full rounded-xl border border-border px-4 py-3 text-slate-900 focus:border-primary focus:outline-none" />
            {date && (
              <div className="mt-6">
                <label className="text-sm font-medium text-slate-700">{t("booking.choose_time")}</label>
                {slots.length === 0 ? <p className="mt-3 text-sm text-slate-400">{t("common.loading")}</p> : (
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                    {slots.map((sl) => (
                      <button key={sl.start} disabled={!sl.available} onClick={() => setStart(sl.start)} data-testid={`slot-${sl.time}`} className={`rounded-lg border py-2.5 text-sm font-medium transition-all ${start === sl.start ? "border-primary bg-primary text-white" : sl.available ? "border-border bg-white text-slate-700 hover:border-primary" : "cursor-not-allowed border-transparent bg-slate-100 text-slate-300 line-through"}`}>
                        {sl.time}
                      </button>
                    ))}
                  </div>
                )}
                {slots.length > 0 && slots.every((s) => !s.available) && <p className="mt-3 text-sm text-[hsl(var(--brand-red))]">{t("booking.no_slots")}</p>}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <Field label={t("common.name")} testid="info-name" value={info.customer_name} onChange={(v) => setInfo({ ...info, customer_name: v })} />
            <Field label={t("common.phone")} testid="info-phone" value={info.customer_phone} onChange={(v) => setInfo({ ...info, customer_phone: v })} placeholder="05xxxxxxxx" dir="ltr" />
            {locationType === "home" && <Field label={t("common.address")} testid="info-address" value={info.address} onChange={(v) => setInfo({ ...info, address: v })} />}
            <div>
              <label className="text-sm font-medium text-slate-700">{t("common.notes")}</label>
              <textarea value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} data-testid="info-notes" rows={3} className="mt-1 block w-full rounded-xl border border-border px-4 py-3 focus:border-primary focus:outline-none" />
            </div>
          </div>
        )}

        {step === 4 && quote && (
          <div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900">{localize(service, "name")}</div>
              <div className="mt-1 text-sm text-slate-500">{fmtDate(start).greg} · {fmtDate(start).time}</div>
              <div className="text-xs text-slate-400">{fmtDate(start).hijri} (هجري)</div>
            </div>
            <div className="mt-4 flex gap-2">
              <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder={isAr ? "كود الخصم" : "Coupon code"} data-testid="coupon-input" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
              <button onClick={refreshQuote} data-testid="apply-coupon-btn" className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:border-primary">{t("common.apply")}</button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {quote.lines.map((l, i) => (
                <div key={i} className="flex justify-between"><span className="text-slate-600">{isAr ? l.label_ar : l.label_en}</span><span className={l.amount < 0 ? "font-medium text-[hsl(var(--brand-red))]" : "text-slate-900"}>{fmtSAR(l.amount)}</span></div>
              ))}
              <div className="flex justify-between border-t border-border pt-2"><span className="text-slate-600">{t("common.vat")} ({Math.round(quote.vat_rate * 100)}%)</span><span>{fmtSAR(quote.vat)}</span></div>
              <div className="flex justify-between text-base font-bold"><span>{t("common.total")}</span><span className="text-primary">{fmtSAR(quote.total)}</span></div>
              {quote.is_peak && <div className="text-xs text-[hsl(var(--brand-orange))]">{t("booking.peak_note")}</div>}
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {[["deposit", `${t("booking.pay_deposit")} · ${fmtSAR(quote.deposit)}`], ["full", `${t("booking.pay_full")} · ${fmtSAR(quote.total)}`]].map(([v, label]) => (
                <button key={v} onClick={() => setPayChoice(v)} data-testid={`paychoice-${v}`} className={`rounded-xl border-2 p-3 text-sm font-medium ${payChoice === v ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-600"}`}>{label}</button>
              ))}
            </div>
            <div className="mt-3">
              <label className="text-sm font-medium text-slate-700">{t("booking.pay_method")}</label>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[["pay_on_arrival", t("booking.pay_arrival")], ["bank_transfer", t("booking.pay_bank")], ["card", isAr ? "بطاقة (تجريبي)" : "Card (demo)"]].map(([v, label]) => (
                  <button key={v} onClick={() => setPayMethod(v)} data-testid={`paymethod-${v}`} className={`rounded-lg border px-3 py-2 text-sm ${payMethod === v ? "border-primary bg-primary/5 text-primary" : "border-border text-slate-600"}`}>{label}</button>
                ))}
              </div>
              <p className="mt-2 text-xs text-amber-600">⚠ {t("booking.demo_pay_note")}</p>
            </div>
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={policyAccepted} onChange={(e) => setPolicyAccepted(e.target.checked)} data-testid="policy-checkbox" className="mt-0.5 h-4 w-4 rounded border-border text-primary" />
              {t("booking.policy_accept")}
            </label>
          </div>
        )}

        <div className="mt-7 flex items-center justify-between">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="booking-back-btn" className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-100">{t("common.back")}</button>
          {step < 3 && <button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} data-testid="booking-next-btn" className="rounded-full bg-primary px-7 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-primary/90 active:scale-95">{t("common.next")}</button>}
          {step === 3 && <button onClick={goReview} disabled={busy || !canNext()} data-testid="booking-review-btn" className="rounded-full bg-primary px-7 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:bg-primary/90 active:scale-95">{busy ? t("common.loading") : t("booking.step_review")}</button>}
          {step === 4 && <button onClick={confirm} disabled={busy || !policyAccepted} data-testid="booking-confirm-btn" className="rounded-full bg-[hsl(var(--brand-orange))] px-7 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:opacity-90 active:scale-95">{busy ? t("common.loading") : t("common.confirm")}</button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, dir, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <input value={value} dir={dir} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 block w-full rounded-xl border border-border px-4 py-3 focus:border-primary focus:outline-none" />
    </div>
  );
}

export function BookingConfirmed() {
  const { t, fmtSAR, fmtDate, isAr } = useI18n();
  const nav = useNavigate();
  const id = window.location.pathname.split("/").pop();
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => {}); }, [id]);
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
      <h1 className="mt-4 text-2xl font-bold text-slate-900">{t("booking.confirmed")}</h1>
      <p className="mt-1 text-slate-500">{t("booking.confirmed_sub")}</p>
      {b && (
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 text-start">
          <div className="flex justify-between text-sm"><span className="text-slate-500">{t("booking.booking_ref")}</span><span className="font-mono font-semibold">{b.id.slice(0, 8).toUpperCase()}</span></div>
          <div className="mt-2 flex justify-between text-sm"><span className="text-slate-500">{t("common.date")}</span><span>{fmtDate(b.start).greg} · {fmtDate(b.start).time}</span></div>
          <div className="mt-2 flex justify-between text-sm"><span className="text-slate-500">{t("common.total")}</span><span className="font-bold text-primary">{fmtSAR(b.amount_total)}</span></div>
          <div className="mt-2 flex justify-between text-sm"><span className="text-slate-500">{t("booking.remaining")}</span><span>{fmtSAR(b.amount_due)}</span></div>
        </div>
      )}
      <button onClick={() => nav("/account")} className="mt-6 rounded-full bg-primary px-7 py-3 font-semibold text-white">{t("account.bookings")}</button>
    </div>
  );
}
